import React, {useState} from 'react'
import {
    Box,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    Button,
    SimpleGrid,
    useStyleConfig,
    VStack,
    HStack,
    Link,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useDisclosure,
    useMediaQuery,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import useCountries from '../../hooks/use-countries'
import ReactCountryFlag from 'react-country-flag'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import CloseIcon from '../icons/action/CloseIcon'
import ChevronUp from '../icons/arrows/ChevronUp'
import WebsiteIcon from '../icons/support/WebsiteIcon'
import {useGetCountrySelector} from '../../custom-api/hooks/use-get-country-selector'
import {AddIcon, MinusIcon} from '../icons/action'
import {remCalc} from '../../theme/_utils'

export const Country = ({locales, countryCode}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const folderFlags = getAssetUrl('static/img/flags/4x3/')

    return (
        <Box {...styles?.countryContainer}>
            <ReactCountryFlag
                countryCode={countryCode}
                svg
                cdnUrl={folderFlags}
                cdnSuffix="svg"
                style={{...styles?.flag}}
            />
            {locales.map((locale, idx) => (
                <Link href={locale?.url} key={idx}>
                    <HStack>
                        <Text {...styles?.textCountry}>{locale?.country}</Text>
                        <Text {...styles?.textCode}>{locale?.lang}</Text>
                    </HStack>
                </Link>
            ))}
        </Box>
    )
}

Country.propTypes = {
    locales: PropTypes.array.isRequired,
    countryCode: PropTypes.string.isRequired,
    intl: PropTypes.object,
}

const SelectorModal = ({isOpen, onClose, intl}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const {data} = useGetCountrySelector()
    const countries = useCountries(data)
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(767.9)})`)
    const [isTablet] = useMediaQuery(
        `(min-width: ${remCalc(768)}) and (max-width: ${remCalc(1024)})`,
    )

    return (
        <Modal
            {...styles?.modalContainer}
            isOpen={isOpen}
            onClose={onClose}
            blockScrollOnMount={false}
        >
            <ModalOverlay />
            <ModalContent {...styles?.selectorContent}>
                <ModalHeader {...styles?.selectorHeader}>
                    <Box {...styles?.closeIconContainer}>
                        <CloseIcon
                            data-testid="close-button"
                            {...styles?.closeIcon}
                            onClick={onClose}
                        />
                    </Box>
                    <Text {...styles?.headerText}>{data?.selector_title}</Text>
                </ModalHeader>
                <ModalBody data-testid="modal-body" {...styles?.modalBody}>
                    {isMobile || isTablet ? (
                        <Accordion allowMultiple>
                            {countries.map((dataSet, idx) => (
                                <AccordionItem key={idx} {...styles.accordionItem}>
                                    {({isExpanded}) => (
                                        <Box {...styles?.region}>
                                            <AccordionButton {...styles.accordionButton}>
                                                <Text {...styles?.textHeader}>
                                                    {dataSet.region}
                                                </Text>
                                                <Box
                                                    transform={
                                                        isExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)'
                                                    }
                                                    transition="transform 0.3s ease"
                                                    {...styles.accordionIcon}
                                                >
                                                    {isExpanded ? (
                                                        <MinusIcon
                                                            w={remCalc(32)}
                                                            h={remCalc(32)}
                                                            color="background.dark"
                                                        />
                                                    ) : (
                                                        <Box p={remCalc(6.4)}>
                                                            <AddIcon
                                                                w={remCalc(32)}
                                                                h={remCalc(32)}
                                                                color="background.dark"
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </AccordionButton>
                                            <AccordionPanel>
                                                <SimpleGrid {...styles?.selectorGrid}>
                                                    {Object.entries(dataSet.countries).map(
                                                        ([countryCode, locales]) => (
                                                            <Country
                                                                key={countryCode}
                                                                locales={locales}
                                                                countryCode={countryCode}
                                                                intl={intl}
                                                            />
                                                        ),
                                                    )}
                                                </SimpleGrid>
                                            </AccordionPanel>
                                        </Box>
                                    )}
                                </AccordionItem>
                            ))}
                        </Accordion>
                    ) : (
                        countries.map((dataSet, idx) => (
                            <Box {...styles?.region} key={idx}>
                                <Text {...styles?.textHeader}>{dataSet.region}</Text>
                                <SimpleGrid {...styles?.selectorGrid}>
                                    {Object.entries(dataSet.countries).map(
                                        ([countryCode, locales]) => (
                                            <Country
                                                key={countryCode}
                                                locales={locales}
                                                countryCode={countryCode}
                                                intl={intl}
                                            />
                                        ),
                                    )}
                                </SimpleGrid>
                            </Box>
                        ))
                    )}
                </ModalBody>
                <ModalFooter {...styles?.selectorFooter}>
                    <VStack {...styles?.footerContent}>
                        <Text {...styles?.textFooterHeader}>{data?.international_title}</Text>
                        <Text {...styles?.footerText}>{data?.international_description}</Text>
                        <Button
                            as="a"
                            w={remCalc(331)}
                            href={data?.international_cta?.href}
                            variant="primary"
                        >
                            {data?.international_cta?.title}
                        </Button>
                    </VStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

SelectorModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    intl: PropTypes.object,
}

const CountrySelector = ({intl}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const folderFlags = getAssetUrl('static/img/flags/4x3/')
    const [selectedLocale] = useState(intl.locale.slice(3))
    return (
        <>
            <Box data-testid="country-button" {...styles?.openButton} onClick={onOpen}>
                <HStack spacing={2} alignItems="center" {...styles?.selectorSubContainer}>
                    {selectedLocale ? (
                        <ReactCountryFlag
                            countryCode={selectedLocale}
                            svg
                            cdnUrl={folderFlags}
                            cdnSuffix="svg"
                            alt={intl.formatMessage({
                                id: 'footer.country_selector_currently_selected_country_icon',
                                defaultMessage:
                                    'A small icon representing the currently selected country',
                            })}
                            style={{
                                width: '100%',
                                height: '100%',
                                maxWidth: '20px',
                                maxHeight: '22px',
                                borderRadius: '50%',
                            }}
                        />
                    ) : (
                        <WebsiteIcon
                            boxSize="20px"
                            color="text.inverse"
                            style={{
                                width: '100%',
                                height: '100%',
                                maxWidth: '20px',
                                maxHeight: '22px',
                                borderRadius: '50%',
                            }}
                        />
                    )}

                    <Text {...styles?.buttonText}>{selectedLocale || 'INT'}</Text>
                    <ChevronUp {...styles?.buttonArrow} />
                </HStack>
            </Box>
            <SelectorModal isOpen={isOpen} onClose={onClose} intl={intl} />
        </>
    )
}

CountrySelector.propTypes = {
    intl: PropTypes.object.isRequired,
}

export default CountrySelector
